import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import {getComponentStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {makeConditionalControl} from '../../controls/conditional-control/conditional-control';
import {ConditionalGatingSource} from '../../controls/conditional-control/conditional-control.types';
import {makeColorControl} from '../../controls/color-control/color-control';
import {cellsBgColorDescriptor, cellsBorderColorDescriptor} from '../../controls/color-control/color-control-descriptors';
import {makeOptionListControl} from '../../controls/option-list-control/option-list-control';
import {verticalAlignDescriptor} from '../../controls/option-list-control/option-list-control-descriptors';
import {makeSliderControl} from '../../controls/slider-control/slider-control';
import {
  borderRadiusDescriptor,
  cellBorderRadiusDescriptor,
  cellBorderWidthDescriptor,
  cellSpacingDescriptor,
  paddingDescriptor,
} from '../../controls/slider-control/slider-control-descriptors';
import {makeStepButtonsControl} from '../../controls/step-buttons-control/step-buttons-control';
import {columnsStepDescriptor, rowsStepDescriptor} from '../../controls/step-buttons-control/step-buttons-control-descriptors';
import {makeStyleHeader} from '../../controls/style-header/style-header';
import {makeUploadButtonControl} from '../../controls/upload-button-control/upload-button-control';
import {backgroundMediaDescriptor} from '../../controls/upload-button-control/upload-button-control-descriptors';
import {registerEmailMakerSector} from '../../right-panel-registry';
import {TileComplexity} from '@EmailMakerReact/lib/email-maker-tile-meta-library.types';
import {deleteControl, duplicateControl, lockControl} from '../shared/common-action-controls';
import {borderStyleControl, conditionalBorderColorControl, conditionalBorderWidthControl} from '../shared/border-controls';
import {bgColorControl} from '../shared/color-controls';
import type {Component} from 'grapesjs';
import type {EmailMakerControl} from '../../right-panel-registry.types';

const cellStylesHeader: EmailMakerControl = makeStyleHeader('pmwjs_cell_styles');
const tableStylesHeader: EmailMakerControl = makeStyleHeader('pmwjs_table_styles');

const rowsStepControl = makeStepButtonsControl(rowsStepDescriptor);
const columnsStepControl = makeStepButtonsControl(columnsStepDescriptor);
const backgroundMediaControl: EmailMakerControl = makeUploadButtonControl(backgroundMediaDescriptor);
const cellsBgColorControl = makeColorControl(cellsBgColorDescriptor);
const cellsBorderColorControl = makeColorControl(cellsBorderColorDescriptor);
const verticalAlignControl = makeOptionListControl(verticalAlignDescriptor);

/** Cell-border-color row is hidden when cell border-width reads back as 0 or unset. */
function isCellBorderColorVisible(component: Component): boolean {
  const raw = getComponentStyleProperty(component, cellBorderWidthDescriptor.property);
  return raw !== '' && parseInt(raw, 10) !== 0;
}

const cellBorderWidthControl = makeSliderControl(cellBorderWidthDescriptor);
const cellBorderRadiusControl = makeSliderControl(cellBorderRadiusDescriptor);
const cellSpacingControl = makeSliderControl(cellSpacingDescriptor);
const borderRadiusControl = makeSliderControl(borderRadiusDescriptor);
const paddingControl = makeSliderControl(paddingDescriptor);

const conditionalCellsBorderColorControl = makeConditionalControl({
  control: cellsBorderColorControl,
  predicate: isCellBorderColorVisible,
  gatingSource: ConditionalGatingSource.STYLE,
  gatingKey: cellBorderWidthDescriptor.property,
});

registerEmailMakerSector(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TABLE, {
  title: window.i18next.t('pmwjs_table'),
  properties: [
    {control: duplicateControl},
    {control: lockControl},
    {control: deleteControl},
    {control: rowsStepControl, tileMeta: {icon: 'icon-list', labelKey: 'pmwjs_rows', complexity: TileComplexity.SIMPLE}},
    {control: columnsStepControl, tileMeta: {icon: 'icon-table', labelKey: 'pmwjs_columns', complexity: TileComplexity.SIMPLE}},
    {control: cellStylesHeader},
    {control: cellsBgColorControl, tileMeta: {icon: 'icon-fill', labelKey: 'pmwjs_background_color', complexity: TileComplexity.SIMPLE}},
    {control: cellBorderWidthControl, tileMeta: {icon: 'icon-stroke', labelKey: 'pmwjs_border', complexity: TileComplexity.SIMPLE}},
    {control: conditionalCellsBorderColorControl, tileMeta: {icon: 'icon-colorpicker', labelKey: 'pmwjs_border_color', complexity: TileComplexity.SIMPLE}},
    {control: cellBorderRadiusControl, tileMeta: {icon: 'icon-corner', labelKey: 'pmwjs_cell_roundness', complexity: TileComplexity.COMPLEX}},
    {control: cellSpacingControl, tileMeta: {icon: 'icon-equal-spacing-horizontal', labelKey: 'pmwjs_cell_spacing', complexity: TileComplexity.SIMPLE}},
    {control: tableStylesHeader},
    {control: backgroundMediaControl, tileMeta: {icon: 'icon-image', labelKey: 'pmwjs_background_media', complexity: TileComplexity.SIMPLE}},
    {control: bgColorControl, tileMeta: {icon: 'icon-fill', labelKey: 'pmwjs_background_color', complexity: TileComplexity.SIMPLE}},
    {control: verticalAlignControl, tileMeta: {icon: 'icon-v-align-center', labelKey: 'pmwjs_vertical_alignment', complexity: TileComplexity.SIMPLE}},
    {control: borderStyleControl, tileMeta: {icon: 'icon-stroke', labelKey: 'pmwjs_border', complexity: TileComplexity.SIMPLE}},
    {control: conditionalBorderColorControl, tileMeta: {icon: 'icon-colorpicker', labelKey: 'pmwjs_border_color', complexity: TileComplexity.SIMPLE}},
    {control: conditionalBorderWidthControl, tileMeta: {icon: 'icon-stroke', labelKey: 'pmwjs_thickness', complexity: TileComplexity.SIMPLE}},
    {control: borderRadiusControl, tileMeta: {icon: 'icon-corner', labelKey: 'pmwjs_roundness', complexity: TileComplexity.COMPLEX}},
    {control: paddingControl, tileMeta: {icon: 'icon-edge-spacing', labelKey: 'pmwjs_spacing', complexity: TileComplexity.COMPLEX}},
  ],
});
